import React, { useRef, useEffect, useState, useMemo } from 'react';
import * as d3 from 'd3';
import { AlertTriangle } from 'lucide-react';

/**
 * DurationHeatmap
 * Same grid language as OutlierHeatmap / CompletenessHeatmap:
 *   columns = data collectors, rows = interview dates (weeks when there are many days),
 *   cell    = median interview duration for that collector on that day.
 * Colour: within the acceptable range = green; outside = yellow -> orange -> red
 * by how far outside the range the median sits.
 *
 * Props (unchanged from the old component so DurationSection needs no edits)
 *   data            duration check results ({ overall, by_collector })
 *   dataCollectorId collector id column name (only used to gate rendering)
 *   durationRange   { min, max } acceptable range
 *   isDarkMode
 *   width           ignored except as a fallback; the chart measures its container
 */

const GREEN = '#10B981';
const OUT_STOPS = ['#c3d334', '#FB923C', '#EF4444'];

const fmtDate = (d) => {
  const dt = new Date(d);
  return dt.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};
const weekStart = (d) => {
  const dt = new Date(d);
  const day = (dt.getDay() + 6) % 7; // Monday = 0
  dt.setDate(dt.getDate() - day);
  dt.setHours(0, 0, 0, 0);
  return dt;
};

const DurationHeatmap = ({
  data,
  dataCollectorId,
  width: propWidth = 800,
  height = null,
  isDarkMode = false,
  durationRange = null,
}) => {
  const ref = useRef();
  const containerRef = useRef();
  const [containerWidth, setContainerWidth] = useState(propWidth);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return undefined;
    const update = () => { if (el.offsetWidth > 0) setContainerWidth(el.offsetWidth); };
    update();
    let ro;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver((entries) => {
        const w = entries[0]?.contentRect?.width;
        if (w > 0) setContainerWidth(w);
      });
      ro.observe(el);
    }
    const onResize = () => requestAnimationFrame(update);
    window.addEventListener('resize', onResize);
    const t = setTimeout(update, 100);
    return () => {
      if (ro) ro.disconnect();
      window.removeEventListener('resize', onResize);
      clearTimeout(t);
    };
  }, []);

  const min = durationRange && typeof durationRange.min === 'number' ? durationRange.min : null;
  const max = durationRange && typeof durationRange.max === 'number' ? durationRange.max : null;

  // Build the cell matrix once per data change
  const model = useMemo(() => {
    const byCollector = data?.by_collector || {};
    const collectors = Object.keys(byCollector).sort();
    if (!collectors.length) return null;

    const records = [];
    collectors.forEach((c) => {
      const tl = byCollector[c]?.timeline_data;
      if (Array.isArray(tl)) {
        tl.forEach((r) => {
          if (r && r.date && r.duration !== null && r.duration !== undefined && !isNaN(+r.duration)) {
            const dt = new Date(r.date);
            if (!isNaN(dt.getTime())) records.push({ collector: c, date: dt, duration: +r.duration });
          }
        });
      }
    });

    let mode = 'day';
    let rows = [];
    let cells = [];

    if (records.length > 0) {
      const days = Array.from(new Set(records.map((r) => r.date.toISOString().slice(0, 10)))).sort();
      mode = days.length > 31 ? 'week' : 'day';
      const keyOf = (dt) => (mode === 'week' ? weekStart(dt).toISOString().slice(0, 10) : dt.toISOString().slice(0, 10));
      const groups = {};
      records.forEach((r) => {
        const k = keyOf(r.date);
        const id = `${r.collector}|${k}`;
        if (!groups[id]) groups[id] = { collector: r.collector, row: k, values: [] };
        groups[id].values.push(r.duration);
      });
      rows = Array.from(new Set(Object.values(groups).map((g) => g.row))).sort();
      cells = Object.values(groups).map((g) => {
        const v = g.values.slice().sort((a, b) => a - b);
        const median = d3.median(v);
        const within = v.filter((x) => (min === null || x >= min) && (max === null || x <= max)).length;
        return {
          collector: g.collector,
          row: g.row,
          value: median,
          n: v.length,
          lo: v[0],
          hi: v[v.length - 1],
          withinPct: (within / v.length) * 100,
        };
      });
    } else {
      // No dated interviews (pre-calculated duration variable): one summary row per statistic
      mode = 'stats';
      const stat = (cd, k) => cd?.[k] ?? cd?.[`${k}_minutes`];
      rows = ['median_duration', 'mean_duration', 'min_duration', 'max_duration'];
      collectors.forEach((c) => {
        rows.forEach((k) => {
          const v = stat(byCollector[c], k);
          if (typeof v === 'number' && !isNaN(v)) {
            cells.push({ collector: c, row: k, value: v, n: byCollector[c]?.valid_observations ?? byCollector[c]?.total_observations ?? null, lo: null, hi: null, withinPct: null });
          }
        });
      });
    }

    const rowLabel = (r) => {
      if (mode === 'stats') return r.replace('_duration', '').replace(/^\w/, (ch) => ch.toUpperCase());
      if (mode === 'week') return `Wk of ${fmtDate(r)}`;
      return fmtDate(r);
    };

    return { collectors, rows, cells, mode, rowLabel };
  }, [data, min, max]);

  // How far outside the range a value sits, 0 (inside) .. 1 (far outside)
  const deviation = (v) => {
    if (v === null || v === undefined || isNaN(v)) return 0;
    if (min !== null && v < min) return Math.min(1, (min - v) / Math.max(min, 1e-9));
    if (max !== null && v > max) return Math.min(1, (v - max) / Math.max(max, 1e-9));
    return 0;
  };
  const outScale = d3.scaleLinear().domain([0, 0.5, 1]).range(OUT_STOPS).interpolate(d3.interpolateRgb.gamma(2.2));
  const colorOf = (v) => {
    const dev = deviation(v);
    if (dev === 0) return GREEN;
    // Lift off green immediately so even a slight miss reads as outside the range
    return outScale(Math.max(0.12, dev));
  };

  useEffect(() => {
    if (!model || !ref.current) return undefined;
    const svg = d3.select(ref.current);
    svg.selectAll('*').remove();

    const { collectors, rows, cells, rowLabel } = model;
    const margin = { top: 20, right: 150, bottom: 130, left: 120 };
    const width = Math.max(containerWidth, 320);

    const availableWidth = width - margin.left - margin.right - 20;
    const cellW = Math.max(30, Math.min(60, availableWidth / collectors.length));
    const cellH = Math.max(30, Math.min(44, cellW * 0.72));

    const innerWidth = cellW * collectors.length;
    const innerHeight = cellH * rows.length;
    const totalWidth = innerWidth + margin.left + margin.right;
    const totalHeight = innerHeight + margin.top + margin.bottom;

    const x = d3.scaleBand().domain(collectors).range([0, innerWidth]).padding(0.08);
    const y = d3.scaleBand().domain(rows).range([0, innerHeight]).padding(0.12);

    const g = svg.append('g').attr('transform', `translate(${margin.left},${margin.top})`);

    const tooltip = d3.select('body').append('div')
      .attr('class', 'duration-heatmap-tooltip')
      .style('position', 'absolute')
      .style('visibility', 'hidden')
      .style('background', isDarkMode
        ? 'linear-gradient(135deg, rgba(23, 23, 27, 0.98) 0%, rgba(13, 14, 14, 0.98) 100%)'
        : 'linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(249, 250, 251, 0.98) 100%)')
      .style('color', isDarkMode ? '#E5E7EB' : '#0F172A')
      .style('padding', '14px 18px')
      .style('border', isDarkMode ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(0, 0, 0, 0.1)')
      .style('border-radius', '12px')
      .style('box-shadow', isDarkMode ? '0 12px 40px rgba(0, 0, 0, 0.4)' : '0 12px 40px rgba(0, 0, 0, 0.15)')
      .style('font-size', '13px')
      .style('font-family', "'Outfit', -apple-system, sans-serif")
      .style('pointer-events', 'none')
      .style('z-index', '1000')
      .style('backdrop-filter', 'blur(20px)');

    // Empty grid cells so a sparse schedule still reads as a grid
    const filled = new Set(cells.map((c) => `${c.collector}|${c.row}`));
    const empties = [];
    collectors.forEach((c) => rows.forEach((r) => { if (!filled.has(`${c}|${r}`)) empties.push({ collector: c, row: r }); }));
    g.selectAll('rect.empty')
      .data(empties)
      .enter()
      .append('rect')
      .attr('class', 'empty')
      .attr('x', (d) => x(d.collector))
      .attr('y', (d) => y(d.row))
      .attr('width', x.bandwidth())
      .attr('height', y.bandwidth())
      .attr('rx', 6).attr('ry', 6)
      .attr('fill', isDarkMode ? 'rgba(255,255,255,0.035)' : 'rgba(0,0,0,0.035)');

    const muted = isDarkMode ? '#94A3B8' : '#64748B';
    const strong = isDarkMode ? '#E2E8F0' : '#1E293B';
    const fmtVal = (v) => (Math.abs(v) >= 100 ? v.toFixed(0) : v.toFixed(1));

    g.selectAll('rect.cell')
      .data(cells)
      .enter()
      .append('rect')
      .attr('class', 'cell')
      .attr('x', (d) => x(d.collector))
      .attr('y', (d) => y(d.row))
      .attr('width', x.bandwidth())
      .attr('height', y.bandwidth())
      .attr('fill', (d) => colorOf(d.value))
      .attr('rx', 6).attr('ry', 6)
      .style('cursor', 'pointer')
      .style('filter', (d) => (deviation(d.value) > 0 ? `drop-shadow(0 2px 8px ${colorOf(d.value)}40)` : 'none'))
      .style('transform-origin', 'center')
      .style('transform-box', 'fill-box')
      .on('mouseover', function (event, d) {
        d3.select(this).transition().duration(200).attr('rx', 8).attr('ry', 8)
          .style('transform', 'scale(1.12)')
          .style('filter', `drop-shadow(0 4px 12px ${colorOf(d.value)}60)`);
        const c = colorOf(d.value);
        const dev = deviation(d.value);
        const status = dev === 0 ? 'Within range' : (min !== null && d.value < min) ? 'Below minimum' : 'Above maximum';
        const row = (label, val, color) => `
          <div style="display:flex;justify-content:space-between;gap:18px;margin-bottom:6px;">
            <span style="color:${muted};">${label}</span>
            <span style="font-weight:600;${color ? `color:${color};` : ''}">${val}</span>
          </div>`;
        tooltip.style('visibility', 'visible').html(`
          <div style="margin-bottom:10px;font-weight:600;font-size:13.5px;border-bottom:1px solid ${isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'};padding-bottom:8px;">
            ${d.collector} &middot; ${rowLabel(d.row)}
          </div>
          ${row(model.mode === 'stats' ? 'Value' : 'Median duration', `${fmtVal(d.value)} min`, c)}
          ${d.n ? row('Interviews', d.n) : ''}
          ${d.lo !== null && d.hi !== null ? row('Shortest / longest', `${fmtVal(d.lo)} / ${fmtVal(d.hi)} min`) : ''}
          ${d.withinPct !== null ? row('Within range', `${d.withinPct.toFixed(0)}%`, d.withinPct >= 80 ? GREEN : null) : ''}
          <div style="margin-top:6px;font-size:11.5px;color:${c};font-weight:600;">${status}</div>
        `);
      })
      .on('mousemove', function (event) {
        tooltip.style('top', `${event.pageY - 10}px`).style('left', `${event.pageX + 12}px`);
      })
      .on('mouseout', function (event, d) {
        d3.select(this).transition().duration(200).attr('rx', 6).attr('ry', 6)
          .style('transform', 'scale(1)')
          .style('filter', deviation(d.value) > 0 ? `drop-shadow(0 2px 8px ${colorOf(d.value)}40)` : 'none');
        tooltip.style('visibility', 'hidden');
      });

    const fontSize = Math.max(9.5, Math.min(12, cellW / 5));
    g.selectAll('text.cell-value')
      .data(cells)
      .enter()
      .append('text')
      .attr('class', 'cell-value')
      .attr('x', (d) => x(d.collector) + x.bandwidth() / 2)
      .attr('y', (d) => y(d.row) + y.bandwidth() / 2)
      .attr('text-anchor', 'middle')
      .attr('dominant-baseline', 'middle')
      .style('font-size', `${fontSize}px`)
      .style('font-weight', '700')
      .style('letter-spacing', '-0.01em')
      .style('font-family', "'Outfit', -apple-system, sans-serif")
      .style('fill', '#17171B')
      .style('pointer-events', 'none')
      .text((d) => fmtVal(d.value));

    // Axes
    const xAxis = g.append('g').attr('transform', `translate(0,${innerHeight})`).call(d3.axisBottom(x));
    xAxis.selectAll('text')
      .style('fill', muted).style('font-size', '12px').style('font-weight', '500')
      .style('text-anchor', 'end').attr('dx', '-0.5em').attr('dy', '0em').attr('transform', 'rotate(-90)');
    xAxis.selectAll('path, line').style('stroke', 'transparent');

    const yAxis = g.append('g').call(d3.axisLeft(y).tickFormat(rowLabel));
    yAxis.selectAll('text').style('fill', muted).style('font-size', '12px').style('font-weight', '500');
    yAxis.selectAll('path, line').style('stroke', 'transparent');

    g.append('text')
      .attr('x', innerWidth / 2).attr('y', innerHeight + margin.bottom - 20)
      .attr('text-anchor', 'middle')
      .style('font-size', '14px').style('font-weight', '700').style('fill', strong).style('letter-spacing', '0.02em')
      .text('Data Collectors');

    g.append('text')
      .attr('transform', 'rotate(-90)')
      .attr('x', -innerHeight / 2).attr('y', -margin.left + 25)
      .attr('text-anchor', 'middle')
      .style('font-size', '14px').style('font-weight', '700').style('fill', strong).style('letter-spacing', '0.02em')
      .text(model.mode === 'stats' ? 'Statistics' : model.mode === 'week' ? 'Interview Week' : 'Interview Date');

    // Legend: green block for within range + gradient for outside
    const legendWidth = 24;
    const legendHeight = Math.min(Math.max(innerHeight * 0.6, 120), 180);
    const legendX = totalWidth - margin.right + 30;
    const legendY = margin.top + Math.max(0, (innerHeight - legendHeight) / 2);

    const defs = svg.append('defs');
    const gradientId = `duration-heatmap-gradient-${Math.random().toString(36).slice(2, 11)}`;
    const gradient = defs.append('linearGradient').attr('id', gradientId)
      .attr('x1', '0%').attr('x2', '0%').attr('y1', '100%').attr('y2', '0%');
    gradient.selectAll('stop')
      .data([
        { offset: '0%', color: GREEN },
        { offset: '22%', color: GREEN },
        { offset: '30%', color: OUT_STOPS[0] },
        { offset: '65%', color: OUT_STOPS[1] },
        { offset: '100%', color: OUT_STOPS[2] },
      ])
      .enter().append('stop').attr('offset', (d) => d.offset).attr('stop-color', (d) => d.color);

    const shadowId = `legend-shadow-${Math.random().toString(36).slice(2, 11)}`;
    defs.append('filter').attr('id', shadowId)
      .append('feDropShadow').attr('dx', 0).attr('dy', 2).attr('stdDeviation', 4).attr('flood-opacity', 0.15);

    const legend = svg.append('g');
    legend.append('rect')
      .attr('x', legendX).attr('y', legendY).attr('width', legendWidth).attr('height', legendHeight)
      .attr('rx', 6).attr('ry', 6)
      .style('fill', `url(#${gradientId})`).style('filter', `url(#${shadowId})`);

    const rangeText = min !== null && max !== null ? `${min}\u2013${max} min`
      : min !== null ? `\u2265 ${min} min` : max !== null ? `\u2264 ${max} min` : 'no range set';
    [
      { y: legendY, text: 'Far outside' },
      { y: legendY + legendHeight * 0.5, text: 'Mildly outside' },
      { y: legendY + legendHeight * 0.89, text: 'Within range' },
    ].forEach((l) => {
      legend.append('text')
        .attr('x', legendX + legendWidth + 10).attr('y', l.y)
        .style('text-anchor', 'start').style('dominant-baseline', 'middle')
        .style('font-size', '11.5px').style('font-weight', '600').style('fill', muted)
        .text(l.text);
    });
    legend.append('text')
      .attr('x', legendX + legendWidth + 10).attr('y', legendY + legendHeight + 16)
      .style('text-anchor', 'start').style('font-size', '11px').style('fill', muted)
      .text(rangeText);
    legend.append('text')
      .attr('x', legendX + legendWidth / 2).attr('y', legendY - 14)
      .style('text-anchor', 'middle').style('font-size', '12px').style('font-weight', '700').style('fill', strong)
      .style('letter-spacing', '0.02em')
      .text('Median (min)');

    svg.attr('width', totalWidth).attr('height', totalHeight);

    return () => { tooltip.remove(); };
  }, [model, containerWidth, isDarkMode, min, max]);

  if (!model || model.cells.length === 0 || !dataCollectorId) {
    return (
      <div style={{
        width: '100%', height: height || 320,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        borderRadius: '12px',
        border: isDarkMode ? '1px solid rgba(255, 255, 255, 0.06)' : '1px solid rgba(0, 0, 0, 0.06)',
        color: isDarkMode ? '#94A3B8' : '#64748B', fontSize: '14px', fontWeight: 500,
      }}>
        <div style={{ textAlign: 'center' }}>
          <AlertTriangle size={32} style={{ marginBottom: '12px', opacity: 0.5 }} />
          <div>No per-collector duration data available for the heatmap</div>
        </div>
      </div>
    );
  }

  return (
    <div className="overflow-scroller" ref={containerRef}
      style={{ width: '100%', maxWidth: '100%', overflow: 'auto', position: 'relative' }}>
      <svg ref={ref} style={{ borderRadius: '12px', display: 'block', minWidth: '100%' }} />
    </div>
  );
};

export default DurationHeatmap;